import { useRouter } from 'next/router';
import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { trackMixpanelEvent } from 'utils/analytics';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';
import { stepsChecker } from 'utils/general';

import { OnboardingCheckerItem } from '@/modules/Onboarding/components/SetupGuide/OnboardingCheckerItem';
import ProgressBar from '@/modules/settings/approvals/components/shared/ProgressBar';
import { AddPropertyField } from '@/modules/settings/property';
import AddEditTeamMember from '@/modules/settings/Team/components/AddEditTeamMember';
import { Typography } from '@/shared/components/atoms';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { permissions } from '@/shared/constant/roles-and-permissions';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import CheckedClipboard from '~/images/shared/MultiCheckedClipboard.svg';

const HomeSetupGuide = () => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { onboardingChecks, user } = useAuth();
  const { openModal, closeModal } = useModal();
  const { push, locale } = useRouter();
  const [selectedAsset] = useStore((store) => store?.asset);

  const homeChecks = {
    hasFields: onboardingChecks?.hasFields,
    hasTeamMembers: onboardingChecks?.hasTeamMembers,
  };
  const currentStep = stepsChecker(homeChecks);
  // TODO: creat a hook to retreve the actions
  return (
    <div className='w-full rounded-2xl bg-background-neutral-99 py-16'>
      <div className='flex w-full items-center justify-between gap-16 border-b-xs border-b-neutralVariant-90 px-24 pb-16'>
        <div className=' flex items-center gap-16'>
          <span className='text-24 text-primary-default'>
            <CheckedClipboard />
          </span>
          <span className='text-18 font-semi-bold'>{t('setup-guide')}</span>
        </div>
        <div
          className='cursor-pointer rounded-xl border-sm border-primary-50 px-16 py-8 text-base font-medium text-primary-default'
          onClick={() => push(routes.onboarding)}
        >
          {t('show-all')}
        </div>
      </div>
      <div className='flex items-center gap-2 px-16 pt-16 pb-2'>
        <div className='w-full'>
          <ProgressBar step={currentStep} stepsNumber={2} />
        </div>
        <div className='flex items-center gap-[0.1rem] text-14'>
          <span>{currentStep}</span>
          <span>/</span>
          <span>2</span>
          <span>{t('completed')}</span>
        </div>
      </div>
      <div className='flex flex-col gap-16 px-16'>
        <OnboardingCheckerItem
          action={() => {
            trackMixpanelEvent('Onboarding_start_add_fields', user);
            openModal(
              <AddPropertyField
                selectedAsset={selectedAsset}
                onSuccess={() => {
                  toast.success(
                    t('field-added-at-dashboard', { ns: 'common' })
                  );
                }}
              />,
              {
                variant: 'full',
                title: (
                  <Typography variant='h2'>
                    {t('fields-and-crops', { ns: 'common' })}
                  </Typography>
                ),
              }
            );
          }}
          description={t('add-your-fields-description')}
          status={onboardingChecks?.hasFields ? 'done' : 'idle'}
          stepNumber={1}
          isDisabled={
            !UserHasPermission({
              role: user?.userGroupId,
              action: permissions.CAN_ADD_FIELD,
            })
          }
          title={t('add-your-fields')}
          moreUrl={
            locale === 'es'
              ? 'https://aydi.helpjuice.com/es_ES/creacion-de-un-nuevo-campo-lote-cuadro-o-parcela'
              : locale === 'fr'
              ? 'https://aydi.helpjuice.com/fr_FR/creacion-de-un-nuevo-campo-lote-cuadro-o-parcela'
              : locale === 'ar'
              ? 'https://aydi.helpjuice.com/ar_SA/creacion-de-un-nuevo-campo-lote-cuadro-o-parcela'
              : 'https://aydi.helpjuice.com/en_US/configurar-tu-cuenta/creacion-de-un-nuevo-campo-lote-cuadro-o-parcela'
          }
        />
        <OnboardingCheckerItem
          action={() => {
            trackMixpanelEvent('Onboarding_start_invite_teams', user);
            openModal(
              <AddEditTeamMember formName='home page onboarding add team member' />,
              {
                title: t('onboarding-step-2-title', { ns: 'common' }),
                variant: 'full',
                onClose: () =>
                  openModal(
                    <ConfirmationModal
                      header={t('going-somewhere-else', { ns: 'common' })}
                      message={t('you-will-lose-all-data', { ns: 'common' })}
                      variant='warning'
                      dismissText={t('continue-editing', { ns: 'common' })}
                      confirmText={t('discard-and-exit', { ns: 'common' })}
                      onDismissRemoveNumber={1}
                      onConfirm={closeModal}
                      buttonsAlignment='col'
                      preferredAction='dismiss'
                    />
                  ),
              }
            );
          }}
          description={t('add-your-team-members-description')}
          status={onboardingChecks?.hasTeamMembers ? 'done' : 'idle'}
          stepNumber={2}
          isDisabled={
            !UserHasPermission({
              role: user?.userGroupId,
              action: permissions.CAN_ADD_USER,
            })
          }
          title={t('add-your-team-members')}
          moreUrl={
            locale === 'es'
              ? 'https://aydi.helpjuice.com/es_ES/anadir-un-usuario'
              : locale === 'fr'
              ? 'https://aydi.helpjuice.com/fr_FR/anadir-un-usuario'
              : locale === 'ar'
              ? 'https://aydi.helpjuice.com/ar_SA/anadir-un-usuario'
              : 'https://aydi.helpjuice.com/en_US/configurar-tu-cuenta/anadir-un-usuario'
          }
        />
      </div>
    </div>
  );
};

export { HomeSetupGuide };
